import { Checkbox, TableCell, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";
import ColumnDivider from "./ColumnDivider";
import { ScheduleEntry, useScheduleContext } from "./ScheduleContext";

type ScheduleRowProps = {
  rowEntries: ScheduleEntry[];
  renderTitle: React.ReactNode;
  children?: React.ReactNode;
};

const ScheduleRowRoot = styled(TableRow)(({ theme }) => ({
  "& .ScheduleRow-title": {
    width: 0,
    whiteSpace: "nowrap",
  },

  "& .ScheduleRow-content": {
    position: "relative",
    overflow: "hidden",
    padding: 0,
    borderLeft: "1px solid",
    borderRight: "1px solid",
    borderLeftColor: theme.palette.divider,
    borderRightColor: theme.palette.divider,
  },

  "&.ScheduleRow-weeks .ScheduleRow-content, &.ScheduleRow-workweeks .ScheduleRow-content": {
    backgroundImage: `repeating-linear-gradient(to right, ${theme.palette.grey[50]}, ${theme.palette.grey[50]} 50%, transparent 50%, transparent)`,
  },

  "&.ScheduleRow-weeks .ScheduleRow-content": {
    backgroundSize: "calc(100% / var(--column-count) / 3.5) 100%",
  },

  "&.ScheduleRow-workweeks .ScheduleRow-content": {
    backgroundSize: "calc(100% / var(--column-count) / 2.5) 100%",
  },
}));

const ScheduleRow: React.FC<ScheduleRowProps> = ({ rowEntries, renderTitle, children }) => {
  const {
    fromDate,
    toDate,
    columns,
    scale,
    enableSelection,
    selectedEntries,
    toggleSelectionMany,
  } = useScheduleContext();

  return (
    <ScheduleRowRoot
      className={[
        "ScheduleRow-root",
        scale === "WEEKS" && "ScheduleRow-weeks",
        scale === "WORKWEEKS" && "ScheduleRow-workweeks",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ "--column-count": columns.length } as React.CSSProperties}
    >
      {enableSelection && (
        <TableCell padding="checkbox">
          <Checkbox
            disabled={!rowEntries.length}
            checked={
              rowEntries.length > 0 &&
              rowEntries.every((entry) => selectedEntries.includes(entry.id))
            }
            indeterminate={
              rowEntries.some((entry) => selectedEntries.includes(entry.id)) &&
              !rowEntries.every((entry) => selectedEntries.includes(entry.id))
            }
            onChange={() => toggleSelectionMany(rowEntries.map((entry) => entry.id))}
          />
        </TableCell>
      )}

      <TableCell className="ScheduleRow-title">{renderTitle}</TableCell>

      <TableCell className="ScheduleRow-content">
        <>
          {/* Skip divider for first column as it is handled by cell borders */}
          {columns.slice(1).map((column) => {
            return (
              <ColumnDivider
                key={column.startDate.toISOString()}
                timestamp={column.startDate.getTime()}
                startDate={fromDate}
                endDate={toDate}
              />
            );
          })}

          {children}
        </>
      </TableCell>
    </ScheduleRowRoot>
  );
};

export default ScheduleRow;
